import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { animated, config, useSpring } from "@react-spring/web";
import React from "react";
import { useAuth } from "../../hooks/useAuth";
// Create properly typed animated div component
const AnimatedDiv = animated("div");
// Modern color palette
const colors = {
    primary: {
        light: "#60a5fa",
        main: "#3b82f6",
        dark: "#2563eb",
    },
    error: {
        main: "#ef4444",
        dark: "#dc2626",
    },
    text: {
        primary: "#111827",
        secondary: "#6b7280",
        light: "#9ca3af",
    },
    bg: {
        white: "#ffffff",
        gray: {
            50: "#f9fafb",
            100: "#f3f4f6",
            200: "#e5e7eb",
        },
    },
};
/**
 * ユーザー情報表示コンポーネント
 *
 * ログイン中のユーザー情報を表示し、ログアウト機能を提供します。
 * 未認証時は何も表示しません。
 *
 * @param onLogout - ログアウト成功時に呼ばれるコールバック
 * @param className - カスタムCSSクラス
 */
export function UserInfo({ onLogout, className }) {
    const { user, logout, isAuthenticated } = useAuth();
    const [isLoggingOut, setIsLoggingOut] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    // フェードイン・スケールアニメーション
    const cardSpring = useSpring({
        from: { opacity: 0, scale: 0.9 },
        to: { opacity: 1, scale: 1 },
        config: config.gentle,
    });
    // ドロップダウンアニメーション
    const dropdownSpring = useSpring({
        opacity: isOpen ? 1 : 0,
        transform: isOpen ? "translateY(0px)" : "translateY(-10px)",
        config: config.stiff,
    });
    if (!isAuthenticated || !user) {
        return null;
    }
    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            onLogout?.();
        }
        catch (error) {
            // ログアウトエラーが発生してもUIは閉じる
            // エラーはコンソールに記録（実際のアプリではエラーハンドリングサービスに送信）
            console.error("Logout failed:", error);
        }
        finally {
            setIsLoggingOut(false);
        }
    };
    const getRoleBadgeStyle = (role) => {
        const roleStyles = {
            admin: { bg: "#f3e8ff", color: "#7c3aed" },
            employee: { bg: "#dbeafe", color: "#2563eb" },
            parttime: { bg: "#d1fae5", color: "#059669" },
            contractor: { bg: "#fef3c7", color: "#d97706" },
        };
        const style = roleStyles[role] || { bg: "#f3f4f6", color: "#6b7280" };
        return {
            fontSize: "12px",
            padding: "4px 10px",
            borderRadius: "9999px",
            fontWeight: "600",
            backgroundColor: style.bg,
            color: style.color,
        };
    };
    return (_jsxs(AnimatedDiv, { style: { ...cardSpring, position: "relative" }, className: className, children: [_jsxs("button", { type: "button", onClick: () => setIsOpen(!isOpen), style: {
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 16px",
                    borderRadius: "16px",
                    backgroundColor: colors.bg.white,
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
                    border: `2px solid ${colors.bg.gray[200]}`,
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                }, "aria-label": `User menu for ${user.user_name}`, onMouseEnter: (e) => {
                    e.currentTarget.style.boxShadow = "0 12px 32px rgba(0, 0, 0, 0.12)";
                    e.currentTarget.style.transform = "scale(1.02)";
                }, onMouseLeave: (e) => {
                    e.currentTarget.style.boxShadow = "0 8px 24px rgba(0, 0, 0, 0.08)";
                    e.currentTarget.style.transform = "scale(1)";
                }, children: [_jsx("div", { style: {
                            width: "44px",
                            height: "44px",
                            borderRadius: "50%",
                            background: `linear-gradient(135deg, ${colors.primary.light}, ${colors.primary.dark})`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontWeight: "700",
                            fontSize: "18px",
                            boxShadow: `0 4px 12px ${colors.primary.main}30`,
                        }, children: user.user_name.charAt(0).toUpperCase() }), _jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-start", flex: 1 }, children: [_jsx("span", { style: { fontWeight: "600", color: colors.text.primary, fontSize: "15px" }, children: user.user_name }), _jsx("span", { style: getRoleBadgeStyle(user.role), children: user.role })] }), _jsx("svg", { "aria-hidden": "true", style: {
                            width: "16px",
                            height: "16px",
                            color: colors.text.secondary,
                            transition: "transform 0.2s ease",
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) })] }), isOpen && (_jsxs(AnimatedDiv, { style: {
                    ...dropdownSpring,
                    position: "absolute",
                    top: "100%",
                    marginTop: "8px",
                    right: 0,
                    width: "280px",
                    overflow: "hidden",
                    borderRadius: "16px",
                    backgroundColor: colors.bg.white,
                    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
                    border: `2px solid ${colors.bg.gray[200]}`,
                }, children: [_jsxs("div", { style: {
                            padding: "20px",
                            borderBottom: `1px solid ${colors.bg.gray[200]}`,
                        }, children: [_jsx("p", { style: {
                                    fontSize: "13px",
                                    color: colors.text.secondary,
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                }, children: "\u30ED\u30B0\u30A4\u30F3\u4E2D" }), _jsx("p", { style: {
                                    fontWeight: "600",
                                    color: colors.text.primary,
                                    fontSize: "16px",
                                    marginBottom: "6px",
                                }, children: user.user_name }), _jsxs("p", { style: { fontSize: "13px", color: colors.text.light }, children: ["\u5F79\u5272:", " ", _jsx("span", { style: { fontWeight: "600", color: colors.text.secondary }, children: user.role })] })] }), _jsx("div", { style: { padding: "12px" }, children: _jsx("button", { type: "button", onClick: handleLogout, disabled: isLoggingOut, style: {
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                                padding: "12px 16px",
                                borderRadius: "10px",
                                color: colors.error.dark,
                                backgroundColor: "transparent",
                                border: "none",
                                cursor: isLoggingOut ? "not-allowed" : "pointer",
                                transition: "background-color 0.2s ease",
                                opacity: isLoggingOut ? 0.6 : 1,
                                fontWeight: "600",
                                fontSize: "14px",
                            }, onMouseEnter: (e) => {
                                if (!isLoggingOut) {
                                    e.currentTarget.style.backgroundColor = "#fef2f2";
                                }
                            }, onMouseLeave: (e) => {
                                if (!isLoggingOut) {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                }
                            }, children: isLoggingOut ? (_jsxs(_Fragment, { children: [_jsxs("svg", { "aria-hidden": "true", style: { width: "16px", height: "16px", animation: "spin 1s linear infinite" }, fill: "none", viewBox: "0 0 24 24", children: [_jsx("circle", { style: { opacity: 0.25 }, cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), _jsx("path", { style: { opacity: 0.75 }, fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), _jsx("span", { children: "\u30ED\u30B0\u30A2\u30A6\u30C8\u4E2D..." })] })) : (_jsxs(_Fragment, { children: [_jsx("svg", { "aria-hidden": "true", style: { width: "16px", height: "16px" }, fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" }) }), _jsx("span", { children: "\u30ED\u30B0\u30A2\u30A6\u30C8" })] })) }) })] })), _jsx("style", { children: `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      ` })] }));
}
